import { ReactComponent as UserIcon } from "../../svg/Usericon.svg";
import { useForm, Controller } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { CustomButton } from "../../components/CustomButton";
import { classNames } from "primereact/utils";
import QRCode from "react-qr-code";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLayout } from "../../context/LayoutContext";
import { subscription } from "../../reducers/authSlice";
import {
  getCompany,
  setCompany,
  updateCompany,
} from "../../reducers/companySlice";
import "./index.css";

function UserProfile() {
  const { user, userSub, conversation } = useSelector((state) => state.authenticate);
  const { company } = useSelector((state) => state.company);
  const dispatch = useDispatch();
  const { toastMessage } = useLayout();
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!user?.companyId) return;

    dispatch(subscription(user.companyId)).catch(() => {});

    dispatch(getCompany(user.companyId))
      .unwrap()
      .then((data) => {
        if (data.company) {
          dispatch(setCompany(data.company));
        }
      })
      .catch(() => {});
  }, [dispatch, user?.companyId]);

  const defaultValues = {
    businessMobno: "",
    personalMobno: "",
    gst: "",
    pan: "",
    billadd: "",
  };

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({ defaultValues });

  useEffect(() => {
    if (!company) return;
    reset({
      businessMobno: company.phone || "",
      personalMobno: company.personalPhone || "",
      gst: company.gstNumber || "",
      pan: company.panNumber || "",
      billadd: company.billingAddress || "",
    });
  }, [company, reset]);

  const getFormErrorMessage = (name) =>
    isEditing && errors[name] ? <small className="p-error">{errors[name].message}</small> : null;

  const profileMetrics = useMemo(
    () => [
      {
        label: "Subscription",
        value: userSub.isUserSub
          ? `${userSub.userSubType} (${userSub.plan})`
          : "No active plan",
      },
      {
        label: "Conversation Quota",
        value: `${conversation.used || 0} / ${conversation.total || 0}`,
      },
      {
        label: "Remaining Credits",
        value: `${conversation.remain || 0}`,
      },
    ],
    [conversation, userSub]
  );

  const onSubmit = async (data) => {
    const updatedData = {
      billingAddress: data.billadd,
      phone: data.businessMobno,
      personalPhone: data.personalMobno,
      gstNumber: data.gst,
      panNumber: data.pan,
    };

    try {
      await dispatch(updateCompany({ companyId: user.companyId, updatedData })).unwrap();
      dispatch(setCompany({ ...company, ...updatedData }));
      toastMessage({ severity: "success", detail: "Profile updated successfully." });
      setIsEditing(false);
    } catch (error) {
      toastMessage({ severity: "error", detail: "Unable to update profile details." });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (company) {
      reset({
        businessMobno: company.phone || "",
        personalMobno: company.personalPhone || "",
        gst: company.gstNumber || "",
        pan: company.panNumber || "",
        billadd: company.billingAddress || "",
      });
    }
  };

  const handleDownloadQr = () => {
    const svg = document.getElementById("generatedQrCode");
    if (!svg) return;

    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`;
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "SkaleBot-QR-Code.svg";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  return (
    <div className="page-shell profile-page">
      <div className="page-header">
        <div className="page-header-main">
          <h1 className="page-title">Company account profile</h1>
        </div>
        <div className="page-actions">
          <span className="status-pill info">
            <i className="pi pi-id-card" />
            Company #{user?.companyId || "-"}
          </span>
        </div>
      </div>

      <div className="surface-panel profile-hero">
        <div className="profile-hero-main">
          <div className="profile-avatar">
            <UserIcon />
          </div>
          <div className="profile-hero-copy">
            <h2>{company?.companyName || "Company profile"}</h2>
          </div>
        </div>
        <div className="profile-hero-meta">
          {profileMetrics.map((metric) => (
            <div className="profile-metric" key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
            </div>
          ))}
        </div>
      </div>

      <div className="profile-layout">
        <div className="surface-panel">
          <div className="surface-panel-header">
            <div>
              <h2 className="surface-panel-title">Business details</h2>
            </div>
            <div className="page-actions">
              {!isEditing ? (
                <CustomButton label="Edit Details" onClick={() => setIsEditing(true)} />
              ) : (
                <>
                  <CustomButton
                    label="Cancel"
                    varient="outline"
                    onClick={handleCancel}
                  />
                  <CustomButton
                    label="Save Changes"
                    onClick={handleSubmit(onSubmit)}
                  />
                </>
              )}
            </div>
          </div>

          <form className="profile-form-grid">
            <div className="field">
              <label
                htmlFor="businessMobno"
                className={classNames({ "p-error": errors.businessMobno })}
              >
                Business Mobile Number
              </label>
              <Controller
                name="businessMobno"
                control={control}
                rules={{ required: "Please enter a business mobile number" }}
                render={({ field, fieldState }) => (
                  <InputText
                    id={field.name}
                    className={classNames({ "p-invalid": fieldState.invalid })}
                    placeholder="Enter business mobile number"
                    {...field}
                    disabled={!isEditing}
                  />
                )}
              />
              {getFormErrorMessage("businessMobno")}
            </div>

            <div className="field">
              <label
                htmlFor="personalMobno"
                className={classNames({ "p-error": errors.personalMobno })}
              >
                Personal Mobile Number
              </label>
              <Controller
                name="personalMobno"
                control={control}
                rules={{ required: "Please enter a personal mobile number" }}
                render={({ field, fieldState }) => (
                  <InputText
                    id={field.name}
                    className={classNames({ "p-invalid": fieldState.invalid })}
                    placeholder="Enter personal mobile number"
                    {...field}
                    disabled={!isEditing}
                  />
                )}
              />
              {getFormErrorMessage("personalMobno")}
            </div>

            <div className="field">
              <label htmlFor="gst" className={classNames({ "p-error": errors.gst })}>
                GST Number
              </label>
              <Controller
                name="gst"
                control={control}
                rules={{ required: "Please enter GST number" }}
                render={({ field, fieldState }) => (
                  <InputText
                    id={field.name}
                    className={classNames({ "p-invalid": fieldState.invalid })}
                    placeholder="Enter GST number"
                    {...field}
                    disabled={!isEditing}
                  />
                )}
              />
              {getFormErrorMessage("gst")}
            </div>

            <div className="field">
              <label htmlFor="pan" className={classNames({ "p-error": errors.pan })}>
                PAN Number
              </label>
              <Controller
                name="pan"
                control={control}
                rules={{ required: "Please enter PAN number" }}
                render={({ field, fieldState }) => (
                  <InputText
                    id={field.name}
                    className={classNames({ "p-invalid": fieldState.invalid })}
                    placeholder="Enter PAN number"
                    {...field}
                    disabled={!isEditing}
                  />
                )}
              />
              {getFormErrorMessage("pan")}
            </div>

            <div className="field field-full">
              <label
                htmlFor="billadd"
                className={classNames({ "p-error": errors.billadd })}
              >
                Billing Address
              </label>
              <Controller
                name="billadd"
                control={control}
                rules={{ required: "Please enter billing address" }}
                render={({ field, fieldState }) => (
                  <InputTextarea
                    id={field.name}
                    className={classNames({ "p-invalid": fieldState.invalid })}
                    placeholder="Enter billing address"
                    {...field}
                    autoResize
                    rows={4}
                    disabled={!isEditing}
                  />
                )}
              />
              {getFormErrorMessage("billadd")}
            </div>
          </form>
        </div>

        <div className="profile-side-column">
          <div className="surface-panel profile-qr-card">
            <div className="surface-panel-header">
              <div>
                <h2 className="surface-panel-title">QR access</h2>
              </div>
            </div>
            <div className="profile-qr-wrap">
              <QRCode
                id="generatedQrCode"
                size={240}
                style={{ height: "100%", width: "100%" }}
                value={`https://wa.me/${company?.phone || ""}`}
              />
            </div>
            <CustomButton
              varient="filled"
              onClick={handleDownloadQr}
              label="Download QR Code"
            />
          </div>

          <div className="surface-panel">
            <div className="surface-panel-header">
              <div>
                <h2 className="surface-panel-title">Deskala sales contact</h2>
              </div>
            </div>
            <div className="detail-grid profile-contact-grid">
              <div className="detail-item">
                <span className="detail-label">Contact Name</span>
                <div className="detail-value">Goldwin Paul</div>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone</span>
                <div className="detail-value">+91 94903 37682</div>
              </div>
              <div className="detail-item">
                <span className="detail-label">Email</span>
                <div className="detail-value">goldwin.paul@deskala.in</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
